import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { t } from "@/lib/i18n";
import "@/lib/i18n_p1";
import { Mail, Trash2, Copy, Users, Crown } from "lucide-react";
import { toast } from "sonner";

export default function Team() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/workspaces/me");
    setData(data);
    setName(data.workspace?.name || "");
  }, []);

  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces/invites", { email, role });
      const link = `${window.location.origin}/invite/${data.token}`;
      try { await navigator.clipboard.writeText(link); } catch { /* ignore */ }
      if (data.email_sent) {
        toast.success(`Invite emailed to ${data.email} (link also copied)`);
      } else if (data.email_configured === false) {
        toast.success(`Invite link copied — email isn't configured yet, paste this into a message: ${data.email}`);
      } else {
        toast.warning(`Invite created — couldn't send email automatically; link copied to clipboard for ${data.email}`);
      }
      setEmail("");
      await load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  const rename = async () => {
    if (!name.trim()) return;
    try {
      await api.put("/workspaces/me", { name });
      toast.success("Workspace renamed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const revoke = async (id) => {
    await api.delete(`/workspaces/invites/${id}`);
    toast.success("Invite revoked");
    await load();
  };

  const removeMember = async (uid) => {
    await api.delete(`/workspaces/members/${uid}`);
    toast.success("Member removed");
    await load();
  };

  const copyInvite = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    toast.success(t(lang,"team.copied"));
  };

  if (!data) return <AppShell><div className="p-8 text-[#868E96]">{t(lang,"common.loading")}</div></AppShell>;

  return (
    <AppShell>
      <div className="editorial-rule pt-4">
        <h1 className="font-display text-4xl">{t(lang,"team.title")}</h1>
      </div>
      <p className="mt-3 text-[#495057] max-w-2xl">{t(lang,"team.sub")}</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E9ECEF] p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1 flex items-center gap-3">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="ws-name-input"
                className="bg-transparent text-2xl font-display flex-1 border-b border-transparent focus:border-[#E9ECEF] focus:outline-none"
              />
              <button onClick={rename} className="text-xs uppercase tracking-[0.18em] text-[#495057] hover:text-[#0A1128]" data-testid="ws-rename-btn">
                {t(lang,"team.rename")}
              </button>
            </div>
          </div>

          <h3 className="text-xs tracking-[0.24em] uppercase font-bold text-[#868E96] mb-3">{t(lang,"team.members")} <span className="text-[#C65D47]">· {data.seats_used}/{data.seat_limit}</span></h3>
          {data.members.length === 0 ? (
            <div className="text-sm text-[#868E96] p-4">{t(lang,"team.no_members")}</div>
          ) : (
            <ul className="divide-y divide-[#E9ECEF] border border-[#E9ECEF]">
              {data.members.map((m) => (
                <li key={m.user_id} className="p-4 flex items-center justify-between" data-testid={`ws-member-${m.user_id}`}>
                  <div className="flex items-center gap-3">
                    {m.role === "owner" ? <Crown className="w-4 h-4 text-[#C65D47]" /> : <Users className="w-4 h-4 text-[#868E96]" />}
                    <div>
                      <div className="text-sm font-medium">{m.email}</div>
                      <div className="text-xs text-[#868E96] uppercase tracking-[0.18em]">{m.role}</div>
                    </div>
                  </div>
                  {m.role !== "owner" && (
                    <button onClick={() => removeMember(m.user_id)} className="text-[#868E96] hover:text-[#C92A2A]" data-testid={`ws-remove-${m.user_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <h3 className="mt-8 text-xs tracking-[0.24em] uppercase font-bold text-[#868E96] mb-3">{t(lang,"team.invites")}</h3>
          {data.pending_invites.length === 0 ? (
            <div className="text-sm text-[#868E96] p-4">{t(lang,"team.no_invites")}</div>
          ) : (
            <ul className="divide-y divide-[#E9ECEF] border border-[#E9ECEF]">
              {data.pending_invites.map((iv) => (
                <li key={iv.invite_id} className="p-4 flex items-center justify-between" data-testid={`ws-invite-${iv.invite_id}`}>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-[#868E96]" />
                    <div>
                      <div className="text-sm font-medium">{iv.email}</div>
                      <div className="text-xs text-[#868E96] uppercase tracking-[0.18em]">{iv.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => copyInvite(iv.token)} className="inline-flex items-center gap-1 text-xs px-3 py-1 border border-[#E9ECEF] hover:bg-[#F8F9FA]" data-testid={`ws-copy-${iv.invite_id}`}>
                      <Copy className="w-3 h-3" /> {t(lang,"team.copy_link")}
                    </button>
                    <button onClick={() => revoke(iv.invite_id)} className="text-[#868E96] hover:text-[#C92A2A]" data-testid={`ws-revoke-${iv.invite_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#E9ECEF] p-6 h-fit">
          <h3 className="font-display text-2xl">{t(lang,"team.invite_email")}</h3>
          <form onSubmit={invite} className="mt-4 space-y-3">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="ws-invite-email"
              placeholder="colleague@company.lb"
              className="w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]"
            />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]" data-testid="ws-invite-role">
              <option value="member">member</option>
              <option value="admin">admin</option>
            </select>
            <button type="submit" disabled={busy || data.seats_used >= data.seat_limit} className="w-full bg-[#0A1128] text-white px-4 py-3 hover:bg-[#1C2538] disabled:opacity-60" data-testid="ws-invite-submit">
              {busy ? t(lang,"common.loading") : t(lang,"team.send")}
            </button>
            {data.seats_used >= data.seat_limit && (
              <p className="text-xs text-[#C65D47]">
                {t(lang,"team.seat_limit_reached")} <Link to="/pricing" className="underline">{t(lang,"pricing.upgrade")}</Link>
              </p>
            )}
          </form>
        </div>
      </div>
    </AppShell>
  );
}
